import { useEffect, useState } from "react";
import { toast } from "sonner";
import { IndianRupee, Wallet, AlertTriangle, CheckCircle2 } from "lucide-react";
import { getPayments, apiErr } from "../lib/api";
import { PageHead, Stat, Empty, Pill } from "./_ui";

const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");

export default function Payments() {
  const [d, setD] = useState(null);
  useEffect(() => { getPayments().then(setD).catch((e) => toast.error(apiErr(e))); }, []);
  const t = d?.totals || {};

  return (
    <div className="mx-auto max-w-[1400px] px-4 py-7 md:px-8" data-testid="admin-payments">
      <PageHead eyebrow="Money movement" title="Payments"
        desc="Transactions from confirmed bookings, platform fee earned and payouts owed to purohits." />

      {d && (
        <div className={`mt-6 flex items-center gap-3 rounded-2xl border px-5 py-4 ${d.cashfreeConnected ? "border-[#CBE3DC] bg-[#EEF6F3]" : "border-[#F3D9B8] bg-[#FDF6EA]"}`} data-testid="payments-cashfree-banner">
          {d.cashfreeConnected ? <CheckCircle2 size={20} className="text-[#2F7A6B]" /> : <AlertTriangle size={20} className="text-[#B98A2E]" />}
          <div>
            <p className="font-body text-sm font-semibold text-[#2C241B]">{d.cashfreeConnected ? "Cashfree connected — live settlements enabled" : "Cashfree not connected"}</p>
            <p className="font-body text-xs text-[#8A8072]">{d.cashfreeConnected ? "Bookings are charged and settled via Cashfree." : "Figures below are computed from confirmed bookings. Add Cashfree keys in Configuration to enable live charging and payouts."}</p>
          </div>
        </div>
      )}

      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Stat icon={IndianRupee} label="Gross volume" value={inr(t.gross)} sub={`${d?.count ?? 0} transactions`} />
        <Stat icon={Wallet} tint={{ bg: "#F0ECF7", fg: "#6A4EA8" }} label="Platform fee" value={inr(t.platformFee)} sub={`At ${t.feePct ?? 10}% take rate`} />
        <Stat icon={IndianRupee} tint={{ bg: "#EEF2E6", fg: "#6D8B44" }} label="Payout to purohits" value={inr(t.payoutToPriests)} />
      </div>

      <div className="mt-6 overflow-hidden rounded-2xl border border-[#E9E2D6] bg-white">
        <div className="hidden grid-cols-12 gap-3 border-b border-[#F0EBE1] bg-[#FAFAF5] px-4 py-3 font-body text-[11px] font-semibold uppercase tracking-wide text-[#A79C8A] md:grid">
          <span className="col-span-3">Ceremony</span><span className="col-span-2">Customer</span><span className="col-span-2">Purohit</span>
          <span className="col-span-2 text-right">Amount</span><span className="col-span-1 text-right">Fee</span><span className="col-span-2 text-right">Payout</span>
        </div>
        {(d?.transactions || []).map((x) => (
          <div key={x.id} data-testid={`payment-row-${x.id}`} className="grid grid-cols-1 items-center gap-2 border-b border-[#F5F1E8] px-4 py-3 last:border-0 md:grid-cols-12 md:gap-3">
            <div className="font-body text-sm font-semibold text-[#2C241B] md:col-span-3">{x.pooja}<span className="block font-normal text-xs text-[#A79C8A]">{x.date}</span></div>
            <div className="font-body text-xs text-[#8A8072] md:col-span-2">{x.customer}</div>
            <div className="font-body text-xs text-[#8A8072] md:col-span-2">{x.priest}</div>
            <div className="font-body text-sm font-semibold text-[#2C241B] md:col-span-2 md:text-right">{inr(x.amount)}</div>
            <div className="font-body text-sm text-[#5C5449] md:col-span-1 md:text-right">{inr(x.fee)}</div>
            <div className="md:col-span-2 md:text-right"><Pill status={x.payout} /></div>
          </div>
        ))}
        {d && d.transactions.length === 0 && <div className="px-4 py-16"><Empty>No transactions yet — confirmed bookings will appear here.</Empty></div>}
      </div>
    </div>
  );
}
